import React, { useState, useEffect, useRef } from 'react';

const VerticalCalendar = ({ events, onDateClick, monthsToShow = 1, startMonth = null }) => {
  const [currentStartMonth, setCurrentStartMonth] = useState(startMonth || new Date());
  const todayRef = useRef(null);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Update start month when startMonth prop changes
  useEffect(() => {
    if (startMonth) {
      const newMonth = new Date(startMonth);
      newMonth.setDate(1);
      setCurrentStartMonth(newMonth);
    }
  }, [startMonth]);

  // Reset to current month when monthsToShow changes
  useEffect(() => {
    if (!startMonth) {
      const newStartMonth = new Date();
      newStartMonth.setDate(1);
      setCurrentStartMonth(newStartMonth);
    }
  }, [monthsToShow, startMonth]);
  
  // Scroll to today on mount and when months change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (todayRef.current) {
        try {
          todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (error) {
          console.log('Could not scroll to today:', error);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [currentStartMonth]);

  // Generate all days for the selected number of months
  const getAllDays = () => {
    const days = [];
    const startDate = new Date(currentStartMonth);
    startDate.setDate(1);
    
    for (let monthOffset = 0; monthOffset < monthsToShow; monthOffset++) {
      const monthDate = new Date(startDate);
      monthDate.setMonth(startDate.getMonth() + monthOffset);
      
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        days.push(date);
      }
    }
    
    return days;
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    if (!date || !events) return { bookings: [], availability: [], adminEvents: [] };
    
    const dateStr = date.toISOString().split('T')[0];
    
    const bookings = events.filter(e => 
      e.type === 'booking' && e.date === dateStr
    );
    
    const availability = events.filter(e => 
      e.type === 'availability' && e.date === dateStr
    );
    
    const adminEvents = events.filter(e => 
      e.type === 'admin_event' && 
      dateStr >= e.start_date && 
      dateStr <= e.end_date
    );
    
    return { bookings, availability, adminEvents };
  };

  // Check if date is today
  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  // Check if date is weekend
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  };

  // Get day name in Danish (abbreviated)
  const getDayName = (date) => {
    const days = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
    return days[date.getDay()];
  };

  // Get month name in Danish
  const getMonthName = (date) => {
    const months = [
      'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'December'
    ];
    return months[date.getMonth()];
  };

  // Scroll to today - ensure we're showing current month first
  const scrollToToday = () => {
    // Set current month to today's month
    const newStartMonth = new Date();
    newStartMonth.setDate(1);
    setCurrentStartMonth(newStartMonth);
    
    // Wait a bit for render, then scroll
    setTimeout(() => {
      if (todayRef.current) {
        try {
          todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (error) {
          console.error('Scroll error:', error);
        }
      }
    }, 100);
  };

  const allDays = getAllDays();
  let currentMonth = null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Vertical day list */}
      <div 
        className="vertical-calendar"
        style={{
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        {allDays.map((date, index) => {
          const dateEvents = getEventsForDate(date);
          const isTodayDate = isToday(date);
          const isWeekendDate = isWeekend(date);
          
          // Show month header when month changes
          const showMonthHeader = date.getDate() === 1;
          
          return (
            <div key={index}>
              {/* Month header */}
              {showMonthHeader && (
                <div 
                  className="month-header"
                  style={{
                    background: '#7c6f5e',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderTop: index > 0 ? '2px solid #f5f5f5' : 'none'
                  }}
                >
                  {getMonthName(date)} {date.getFullYear()}
                </div>
              )}
              
              {/* Day row */}
              <div
                ref={isTodayDate ? todayRef : null}
                onClick={() => onDateClick && onDateClick(date.toISOString().split('T')[0])}
                className="day-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 80px 1fr',
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #e5e5e5',
                  background: isWeekendDate ? '#f9f9f9' : 'white',
                  cursor: onDateClick ? 'pointer' : 'default',
                  transition: 'background 0.2s',
                  ...(isTodayDate && {
                    background: '#fff8e1',
                    borderLeft: '4px solid #ffa726'
                  })
                }}
                onMouseOver={(e) => {
                  if (onDateClick && !isTodayDate) {
                    e.currentTarget.style.background = isWeekendDate ? '#f0f0f0' : '#fafafa';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isTodayDate) {
                    e.currentTarget.style.background = isWeekendDate ? '#f9f9f9' : 'white';
                  }
                }}
              >
                {/* Date number */}
                <div 
                  className="date-number"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: isTodayDate ? '#f57c00' : isWeekendDate ? '#999' : '#333',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {date.getDate()}
                </div>
                
                {/* Day name */}
                <div 
                  className="day-name"
                  style={{
                    fontSize: '1rem',
                    color: isWeekendDate ? '#999' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 500
                  }}
                >
                  {getDayName(date)}
                </div>
                
                {/* Events summary */}
                <div 
                  className="events-summary"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem',
                    fontSize: '0.85rem'
                  }}
                >
                  {/* Admin events */}
                  {dateEvents.adminEvents.map((event, i) => (
                    <div
                      key={`admin-${i}`}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: event.color || '#e3f2fd',
                        borderRadius: '3px',
                        color: '#1976d2',
                        fontWeight: 500
                      }}
                    >
                      🎯 {event.title}
                    </div>
                  ))}
                  
                  {/* Bookings */}
                  {dateEvents.bookings.length > 0 && (() => {
                    // Determine color based on instructor and type
                    const firstBooking = dateEvents.bookings[0];
                    let bgColor = '#e8f5e9';
                    let textColor = '#2e7d32';
                    
                    if (firstBooking.instructor_id === 'oscar') {
                      bgColor = '#e3f2fd';  // Blå for Oscar
                      textColor = '#1976d2';
                    } else if (firstBooking.instructor_id === 'julie-christiansen' && firstBooking.booking_type === 'greven') {
                      bgColor = '#e8f5e9';  // Grøn for Julie Greven
                      textColor = '#2e7d32';
                    } else if (firstBooking.booking_type === 'weekend_course' || firstBooking.booking_type === 'weekend-external') {
                      bgColor = '#f3e5f5';  // Lilla for kurser
                      textColor = '#7b1fa2';
                    }
                    
                    return (
                      <div
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: bgColor,
                          borderRadius: '3px',
                          color: textColor
                        }}
                      >
                        📅 {dateEvents.bookings.length} booking{dateEvents.bookings.length > 1 ? 'er' : ''}
                      </div>
                    );
                  })()}
                  
                  {/* Availability */}
                  {dateEvents.availability.length > 0 && (
                    <div style={{
                      padding: '0.25rem 0.5rem',
                      background: '#fff3e0',
                      borderRadius: '3px',
                      color: '#f57c00'
                    }}>
                      ⏰ Tilgængelig
                    </div>
                  )}
                  
                  {/* No events */}
                  {dateEvents.bookings.length === 0 && 
                   dateEvents.availability.length === 0 && 
                   dateEvents.adminEvents.length === 0 && (
                    <span style={{ color: '#ccc', fontStyle: 'italic' }}>Ingen begivenheder</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VerticalCalendar;
